import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { authFeature } from '../../features/auth/state';
import { AuthActions } from '../../features/auth/state/auth.actions';
import { environment } from '../../../environments/environment';
import { AuthSessionSyncService } from '../services/auth-session-sync.service';
import { TokenRefreshService } from '../services/token-refresh.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const store = inject(Store);
  const injector = inject(Injector);
  const sessionSync = inject(AuthSessionSyncService);
  const token = store.selectSignal(authFeature.selectToken);
  const authToken = token() ?? sessionSync.getSessionValue('auth_token');
  const requestPath = req.url.split('?')[0];
  const isLoginRequest = /\/auth\/?$/.test(requestPath);
  const isRefreshRequest = /\/auth\/refresh\/?$/.test(requestPath);
  const isPasswordRecoveryRequest =
    /\/auth\/forgot-password\/?$/.test(requestPath) ||
    /\/auth\/reset-password\/?$/.test(requestPath) ||
    /\/auth\/reset-password\/validate\/?$/.test(requestPath);
  const isPublicAuthRequest = isLoginRequest || isRefreshRequest || isPasswordRecoveryRequest;
  const isOptionalAuthorizationRequest = req.url.includes('/notifications');
  const isApiRequest = isRequestForApi(req.url, environment.apiUrl);
  const isMePermissionsRequest = /\/me\/permissions\/?$/.test(requestPath);

  const request =
    authToken && isApiRequest && !isPublicAuthRequest ? withBearer(req, authToken) : req;

  return next(request).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        authToken &&
        isApiRequest &&
        !isPublicAuthRequest &&
        !isOptionalAuthorizationRequest
      ) {
        return renewAndReplay(error, req, next, authToken, token, injector);
      }

      // An unexpected 403 signals that access changed mid-session (e.g. a
      // role was revoked); refresh permissions from the endpoint. Guard
      // against recursion when the failing request IS /me/permissions.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        isApiRequest &&
        !isPublicAuthRequest &&
        !isMePermissionsRequest
      ) {
        store.dispatch(AuthActions.loadAccessProfile({ navigateOnComplete: false }));
      }

      return throwError(() => error);
    }),
  );
};

function renewAndReplay(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: (req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>,
  failedToken: string,
  currentToken: () => string | null,
  injector: Injector,
): Observable<HttpEvent<unknown>> {
  const refresh = injector.get(TokenRefreshService);

  const replay = (bearer: string) =>
    next(withBearer(req, bearer)).pipe(
      catchError((replayError) => {
        if (replayError instanceof HttpErrorResponse && replayError.status === 401) {
          refresh.endSession();
        }

        return throwError(() => replayError);
      }),
    );

  const latest = currentToken();

  if (latest && latest !== failedToken) {
    return replay(latest);
  }

  return refresh
    .renew()
    .pipe(
      switchMap((outcome) =>
        outcome.status === 'renewed' ? replay(outcome.token) : throwError(() => error),
      ),
    );
}

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function isRequestForApi(requestUrl: string, apiUrl: string): boolean {
  if (!/^https?:\/\//i.test(requestUrl)) {
    return true;
  }

  try {
    return new URL(requestUrl).origin === new URL(apiUrl).origin;
  } catch {
    return false;
  }
}
